import React, { useMemo } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Link, graphql, useStaticQuery } from 'gatsby';
import cn from 'classnames';
import { SectionHeader } from '../ui/SectionHeader';
import { EmojiFlag } from '../ui/EmojiFlag';

export const ColivingSection: React.FC = () => {
  const { nomadago, images } = useStaticQuery(graphql`
    query colivingSection {
      nomadago {
        portugal: colivings(countryCode: "PT") {
          page {
            total
          }
        }
        colombia: colivings(countryCode: "CO") {
          page {
            total
          }
        }
        es: colivings(countryCode: "ES") {
          page {
            total
          }
        }
        indonesia: colivings(countryCode: "ID") {
          page {
            total
          }
        }
      }
      images: allFile(filter: { relativeDirectory: { eq: "countries" } }) {
        edges {
          node {
            name
            childImageSharp {
              gatsbyImageData(
                width: 700
                placeholder: BLURRED
                layout: CONSTRAINED
              )
            }
          }
        }
      }
    }
  `);

  const featuredCountries = useMemo(() => {
    const colivingImages = images.edges.reduce((acc: any, { node }: any) => {
      acc[node.name] = node.childImageSharp.gatsbyImageData;

      return acc;
    }, {});

    return [
      {
        country: 'Portugal',
        iso: 'PT',
        landingPath: 'portugal',
        image: colivingImages.PT,
        total: nomadago.portugal.page.total,
      },
      {
        country: 'Colombia',
        iso: 'CO',
        landingPath: 'colombia',
        image: colivingImages.CO,
        total: nomadago.colombia.page.total,
      },
      {
        country: 'Spain',
        iso: 'ES',
        landingPath: 'spain',
        image: colivingImages.ES,
        total: nomadago.es.page.total,
      },
      {
        country: 'Indonesia',
        iso: 'ID',
        landingPath: 'indonesia',
        image: colivingImages.ID,
        total: nomadago.indonesia.page.total,
      },
    ];
  }, [nomadago]);

  return (
    <section className="container py-2">
      <SectionHeader
        heading="Find Community with Verified Colivings"
        subheading="Browse our selection of trusted coliving destinations optimized for digital nomads seeking authentic community experiences and seamless living."
      />

      <div className="-mx-4 overflow-auto">
        <ul className="px-4 grid grid-cols-4 w-[300%] list-none sm:w-auto gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 mt-6 mb-8 sm:mt-12 sm:mb-14">
          {featuredCountries.map((country, index) => (
            <li
              key={country.iso}
              className={cn(
                'relative w-full overflow-hidden rounded shadow hover:shadow-lg transition-shadow bg-white group duration-300',
                {
                  'lg:col-span-2 lg:row-span-2': index === 0,
                  'lg:col-span-2 pb-[100%] lg:pb-[50%]': index === 3,
                  'pb-[100%]': index !== 3,
                },
              )}
            >
              <Link to={`/coliving/${country.landingPath}/`}>
                <div className="absolute inset-0 transition-opacity group-hover:opacity-90">
                  <GatsbyImage
                    className="object-cover w-full h-full"
                    image={country.image}
                    alt={`Colivings in ${country.country}`}
                  />
                </div>

                <div className="absolute px-3 py-2 transition-all rounded-lg bg-blue-50 left-2 bottom-2 group-hover:opacity-100 hover:bg-blue-100">
                  <h4 className="text-lg text-blue-500">
                    <EmojiFlag iso={country.iso} /> {country.country}
                  </h4>

                  <p className="text-sm font-medium text-blue-300">
                    Browse{' '}
                    <span className="font-semibold text-blue-400">
                      {country.total}
                    </span>{' '}
                    Colivings
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex justify-center">
        <Link
          to="/coliving/"
          className="w-full button button--primary sm:max-w-60"
        >
          Browse All Colivings
        </Link>
      </div>
    </section>
  );
};
